import React, { useState, useEffect, JSX } from 'react';
import { adminAPI } from './services/api';
import './NotificationSettingsTab.css';

interface EmailSettings {
  is_active: boolean;
  provider: string;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_password: string;
  from_email: string;
  from_name: string;
  connection_status: string;
  last_error?: string;
  last_test_at?: string;
}

interface EmailTemplate {
  id: number;
  template_key: string;
  subject: string;
  body_html: string;
  body_text: string;
  is_active: boolean;
  variables: string[];
}

interface WebhookSettings {
  is_active: boolean;
  webhook_url: string;
  webhook_secret: string;
  webhook_events: string[];
  connection_status: string;
  last_error?: string;
  last_test_at?: string;
}

const TEMPLATE_LABELS: Record<string, { icon: string; name: string; description: string }> = {
  reservation_confirmed: { 
    icon: '📋', 
    name: 'Oda Rezervasyonu Onayı', 
    description: 'Müşteri oda rezervasyonu tamamlayınca gönderilir' 
  },
  payment_received: { 
    icon: '💳', 
    name: 'Ödeme Alındı', 
    description: 'Ödeme başarılı olduğunda gönderilir' 
  },
  sunbed_confirmed: { 
    icon: '🏖️', 
    name: 'Şezlong Rezervasyonu', 
    description: 'Şezlong rezervasyonu onaylandığında gönderilir' 
  },
  breakfast_confirmed: { 
    icon: '🍳', 
    name: 'Kahvaltı Rezervasyonu', 
    description: 'Kahvaltı rezervasyonu onaylandığında gönderilir' 
  },
  dinner_confirmed: { 
    icon: '🍽️', 
    name: 'Akşam Yemeği Rezervasyonu', 
    description: 'Akşam yemeği rezervasyonu onaylandığında gönderilir' 
  }
};

const PROVIDER_PRESETS: Record<string, { name: string; host: string; port: number; secure: boolean }> = {
  gmail: { name: 'Gmail', host: 'smtp.gmail.com', port: 587, secure: false },
  outlook: { name: 'Outlook / Office 365', host: 'smtp.office365.com', port: 587, secure: false },
  yahoo: { name: 'Yahoo Mail', host: 'smtp.mail.yahoo.com', port: 587, secure: false },
  yandex: { name: 'Yandex Mail', host: 'smtp.yandex.com', port: 465, secure: true },
  custom: { name: 'Özel SMTP Sunucu', host: '', port: 587, secure: false }
};

const NotificationSettingsTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'email' | 'webhook' | 'templates'>('email');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Email settings state
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    is_active: false,
    provider: 'custom',
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    connection_status: 'not_tested'
  });

  // Templates state
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templatePreview, setTemplatePreview] = useState<{ subject: string; html: string } | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Test email state
  const [testEmail, setTestEmail] = useState('');

  // Webhook settings state
  const [webhookSettings, setWebhookSettings] = useState<WebhookSettings>({
    is_active: false,
    webhook_url: '',
    webhook_secret: '',
    webhook_events: ['reservation_confirmed', 'payment_received'],
    connection_status: 'not_tested'
  });

  useEffect(() => {
    loadEmailSettings();
    loadWebhookSettings();
    loadTemplates();
  }, []);

  const loadEmailSettings = async () => {
    try {
      const response = await adminAPI.get('/notifications/email');
      if (response.data.success) {
        setEmailSettings(response.data.data);
      }
    } catch (error: any) {
      console.error('Email ayarları yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await adminAPI.get('/notifications/email/templates');
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error: any) {
      console.error('Şablonlar yüklenemedi:', error);
    }
  };

  const handleProviderChange = (provider: string) => {
    const preset = PROVIDER_PRESETS[provider];
    setEmailSettings(prev => ({
      ...prev,
      provider,
      smtp_host: preset.host,
      smtp_port: preset.port,
      smtp_secure: preset.secure
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Sadece gerekli alanları gönder (id, connection_status gibi readonly alanları çıkar)
      const payload = {
        is_active: emailSettings.is_active,
        provider: emailSettings.provider,
        smtp_host: emailSettings.smtp_host,
        smtp_port: emailSettings.smtp_port,
        smtp_secure: emailSettings.smtp_secure,
        smtp_user: emailSettings.smtp_user,
        smtp_password: emailSettings.smtp_password,
        from_email: emailSettings.from_email,
        from_name: emailSettings.from_name
      };
      
      const response = await adminAPI.post('/notifications/email', payload);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Ayarlar kaydedildi!' });
        loadEmailSettings();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Kayıt başarısız' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Bir hata oluştu' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const response = await adminAPI.post('/notifications/email/test-connection');
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ SMTP bağlantısı başarılı!' });
        loadEmailSettings();
      } else {
        setMessage({ type: 'error', text: `❌ Bağlantı başarısız: ${response.data.error}` });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Bağlantı testi başarısız' });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Test için e-posta adresi girin' });
      return;
    }
    setTesting(true);
    setMessage(null);
    try {
      const response = await adminAPI.post('/notifications/email/send-test', { email: testEmail });
      if (response.data.success) {
        setMessage({ type: 'success', text: `✅ Test e-postası ${testEmail} adresine gönderildi!` });
      } else {
        setMessage({ type: 'error', text: `❌ Gönderilemedi: ${response.data.error}` });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Test e-postası gönderilemedi' });
    } finally {
      setTesting(false);
    }
  };

  const handlePreviewTemplate = async (templateKey: string) => {
    try {
      const response = await adminAPI.post(`/notifications/email/templates/${templateKey}/preview`);
      if (response.data.success) {
        setSelectedTemplate(templateKey);
        setTemplatePreview(response.data.data);
        setEditingTemplate(null);
      }
    } catch (error) {
      console.error('Önizleme yüklenemedi:', error);
    }
  };

  const handleEditTemplate = async (templateKey: string) => {
    try {
      const response = await adminAPI.get(`/notifications/email/templates/${templateKey}`);
      if (response.data.success) {
        setEditingTemplate(response.data.data);
        setSelectedTemplate(templateKey);
        setTemplatePreview(null);
      }
    } catch (error) {
      console.error('Şablon yüklenemedi:', error);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    setMessage(null);
    try {
      const response = await adminAPI.put(`/notifications/email/templates/${editingTemplate.template_key}`, {
        subject: editingTemplate.subject,
        body_html: editingTemplate.body_html,
        body_text: editingTemplate.body_text,
        is_active: editingTemplate.is_active
      });
      
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Şablon kaydedildi!' });
        loadTemplates();
        setEditingTemplate(null);
        handlePreviewTemplate(editingTemplate.template_key);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Şablon kaydedilemedi' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleToggleTemplate = async (templateKey: string, isActive: boolean) => {
    try {
      await adminAPI.put(`/notifications/email/templates/${templateKey}`, {
        is_active: isActive
      });
      loadTemplates();
      setMessage({ type: 'success', text: isActive ? '✅ Şablon aktif edildi' : '⚠️ Şablon pasif yapıldı' });
    } catch (error) {
      console.error('Şablon durumu değiştirilemedi:', error);
    }
  };

  // ============ WEBHOOK FONKSİYONLARI ============

  const loadWebhookSettings = async () => {
    try {
      const response = await adminAPI.get('/notifications/webhook');
      if (response.data.success) {
        setWebhookSettings(response.data.data);
      }
    } catch (error: any) {
      console.error('Webhook ayarları yüklenemedi:', error);
    }
  };

  const handleSaveWebhookSettings = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Sadece gerekli alanları gönder
      const payload = {
        is_active: webhookSettings.is_active,
        webhook_url: webhookSettings.webhook_url,
        webhook_secret: webhookSettings.webhook_secret,
        webhook_events: webhookSettings.webhook_events
      };
      
      const response = await adminAPI.post('/notifications/webhook', payload);
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Webhook ayarları kaydedildi!' });
        loadWebhookSettings();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Kayıt başarısız' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Bir hata oluştu' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const response = await adminAPI.post('/notifications/webhook/test', {
        webhook_url: webhookSettings.webhook_url,
        webhook_secret: webhookSettings.webhook_secret
      });
      if (response.data.success) {
        setMessage({ type: 'success', text: '✅ Webhook testi başarılı!' });
      } else {
        setMessage({ type: 'error', text: `❌ Test başarısız: ${response.data.error}` });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Webhook test edilemedi' });
    } finally {
      setTesting(false);
    }
  };

  const handleToggleWebhookEvent = (event: string) => {
    setWebhookSettings(prev => {
      const events = prev.webhook_events.includes(event)
        ? prev.webhook_events.filter(e => e !== event)
        : [...prev.webhook_events, event];
      return { ...prev, webhook_events: events };
    });
  };

  const getStatusBadge = (): JSX.Element => {
    switch (emailSettings.connection_status) {
      case 'connected':
        return <span className="status-badge connected">✓ Bağlı</span>;
      case 'failed':
        return <span className="status-badge failed">✕ Başarısız</span>;
      default:
        return <span className="status-badge not-tested">○ Test Edilmedi</span>;
    }
  };

  if (loading) {
    return <div className="notification-settings loading">Yükleniyor...</div>;
  }

  return (
    <div className="notification-settings">
      <div className="settings-header">
        <h2>📧 Bildirim Ayarları</h2>
        <p>E-posta, SMS ve WhatsApp bildirimlerini yapılandırın</p>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        <button 
          className={`tab-btn ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          📧 E-posta Ayarları
        </button>
        <button 
          className={`tab-btn ${activeTab === 'webhook' ? 'active' : ''}`}
          onClick={() => setActiveTab('webhook')}
        >
          🔔 Webhook Bildirimleri
        </button>
        <button 
          className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          📝 E-posta Şablonları
        </button>
      </div>

      {message && (
        <div className={`message-box ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Email Settings Tab */}
      {activeTab === 'email' && (
        <div className="tab-content">
          {/* Active Toggle */}
          <div className="setting-card">
            <div className="setting-row toggle-row">
              <div className="setting-info">
                <h3>E-posta Bildirimleri</h3>
                <p>Rezervasyon onayları ve bildirimler için e-posta gönderimi</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={emailSettings.is_active}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="connection-status">
              Durum: {getStatusBadge()}
              {emailSettings.last_error && (
                <span className="last-error">Son hata: {emailSettings.last_error}</span>
              )}
            </div>
          </div>

          {/* Provider Selection */}
          <div className="setting-card">
            <h3>E-posta Sağlayıcı</h3>
            <div className="provider-grid">
              {Object.entries(PROVIDER_PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  className={`provider-btn ${emailSettings.provider === key ? 'active' : ''}`}
                  onClick={() => handleProviderChange(key)}
                >
                  {key === 'gmail' && '📧'}
                  {key === 'outlook' && '📨'}
                  {key === 'yahoo' && '📩'}
                  {key === 'yandex' && '📬'}
                  {key === 'custom' && '⚙️'}
                  <span>{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* SMTP Settings */}
          <div className="setting-card">
            <h3>SMTP Ayarları</h3>
            
            {emailSettings.provider === 'custom' && (
              <div className="form-row">
                <div className="form-group">
                  <label>SMTP Sunucu</label>
                  <input 
                    type="text" 
                    placeholder="mail.example.com"
                    value={emailSettings.smtp_host}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                  />
                </div>
                <div className="form-group small">
                  <label>Port</label>
                  <input 
                    type="number" 
                    value={emailSettings.smtp_port}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="form-group small">
                  <label>SSL/TLS</label>
                  <select 
                    value={emailSettings.smtp_secure ? 'true' : 'false'}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_secure: e.target.value === 'true' }))}
                  >
                    <option value="false">STARTTLS (587)</option>
                    <option value="true">SSL (465)</option>
                  </select>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>E-posta Adresi / Kullanıcı Adı</label>
                <input 
                  type="email" 
                  placeholder="info@villapolikne.com"
                  value={emailSettings.smtp_user}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Şifre / Uygulama Şifresi</label>
                <input 
                  type="password" 
                  placeholder="••••••••"
                  value={emailSettings.smtp_password}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                />
              </div>
            </div>

            <div className="info-box">
              <strong>💡 Gmail için:</strong> Normal şifreniz çalışmaz. 
              <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">
                Uygulama şifresi
              </a> oluşturmanız gerekir.
            </div>
          </div>

          {/* Sender Info */}
          <div className="setting-card">
            <h3>Gönderen Bilgileri</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Gönderen Adı</label>
                <input 
                  type="text" 
                  placeholder="Villa Polikne"
                  value={emailSettings.from_name}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, from_name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Gönderen E-posta (isteğe bağlı)</label>
                <input 
                  type="email" 
                  placeholder="Boşsa SMTP e-posta kullanılır"
                  value={emailSettings.from_email}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, from_email: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Test Section */}
          <div className="setting-card test-section">
            <h3>Bağlantı Testi</h3>
            <div className="test-row">
              <button 
                className="btn-test"
                onClick={handleTestConnection}
                disabled={testing || !emailSettings.smtp_user}
              >
                {testing ? '⏳ Test ediliyor...' : '🔌 Bağlantıyı Test Et'}
              </button>
            </div>
            
            <div className="form-row test-email-row">
              <div className="form-group">
                <input 
                  type="email" 
                  placeholder="test@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>
              <button 
                className="btn-send-test"
                onClick={handleSendTestEmail}
                disabled={testing || !testEmail}
              >
                📤 Test E-postası Gönder
              </button>
            </div>
          </div>

          {/* Save Button */}
          <div className="actions">
            <button 
              className="btn-save"
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? 'Kaydediliyor...' : '💾 Ayarları Kaydet'}
            </button>
          </div>
        </div>
      )}

      {/* Webhook Tab */}
      {activeTab === 'webhook' && (
        <div className="tab-content">
          {/* Info Box */}
          <div className="templates-info-box">
            <h4>🔔 Webhook Bildirimleri Nedir?</h4>
            <p>Rezervasyonlar gerçekleştiğinde n8n, Zapier veya kendi sisteminize anında bildirim gönderebilirsiniz. 
               Bu sayede WhatsApp, SMS, Telegram veya başka kanallardan işletme sahibine bildirim yapabilirsiniz.</p>
          </div>

          {/* Active Toggle */}
          <div className="setting-card">
            <div className="setting-row toggle-row">
              <div className="setting-info">
                <h3>Webhook Bildirimleri</h3>
                <p>Rezervasyon tamamlandığında webhook URL'e POST isteği gönder</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={webhookSettings.is_active}
                  onChange={(e) => setWebhookSettings(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Webhook URL */}
          <div className="setting-card">
            <h3>Webhook Ayarları</h3>
            <div className="form-group">
              <label>Webhook URL <span style={{color: 'red'}}>*</span></label>
              <input 
                type="url" 
                placeholder="https://n8n.example.com/webhook/..." 
                value={webhookSettings.webhook_url}
                onChange={(e) => setWebhookSettings(prev => ({ ...prev, webhook_url: e.target.value }))}
              />
              <small>n8n, Zapier veya kendi sisteminizin webhook URL'i</small>
            </div>

            <div className="form-group">
              <label>Webhook Secret (İsteğe Bağlı)</label>
              <input 
                type="password" 
                placeholder="Güvenlik için gizli anahtar"
                value={webhookSettings.webhook_secret}
                onChange={(e) => setWebhookSettings(prev => ({ ...prev, webhook_secret: e.target.value }))}
              />
              <small>HMAC-SHA256 imzası için kullanılır (Header: X-Webhook-Signature)</small>
            </div>
          </div>

          {/* Event Selection */}
          <div className="setting-card">
            <h3>Hangi Olaylar Bildirilsin?</h3>
            <div className="webhook-events">
              {Object.entries(TEMPLATE_LABELS).map(([key, label]) => (
                <label key={key} className="event-checkbox">
                  <input 
                    type="checkbox"
                    checked={webhookSettings.webhook_events.includes(key)}
                    onChange={() => handleToggleWebhookEvent(key)}
                  />
                  <span className="event-label">
                    <span className="event-icon">{label.icon}</span>
                    <span className="event-name">{label.name}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Test Section */}
          <div className="setting-card test-section">
            <h3>Webhook Testi</h3>
            <p>Webhook URL'nizin çalıştığını test edin. Test verisi gönderilecektir.</p>
            <button 
              className="btn-test"
              onClick={handleTestWebhook}
              disabled={testing || !webhookSettings.webhook_url}
            >
              {testing ? '⏳ Test ediliyor...' : '🧪 Webhook Test Et'}
            </button>
          </div>

          {/* Payload Example */}
          <div className="setting-card">
            <h3>📦 Gönderilecek Veri Formatı (JSON)</h3>
            <pre style={{
              background: '#f5f5f5',
              padding: '15px',
              borderRadius: '8px',
              fontSize: '13px',
              overflow: 'auto'
            }}>
{`// ODA REZERVASYONU (Kahvaltı + Akşam Yemeği Dahil)
{
  "event": "reservation_confirmed",
  "timestamp": "2026-02-16T10:30:00.000Z",
  "data": {
    "reservation_id": "BOOKING123456789",
    "booking_id": 42,
    "customer_name": "Ahmet Yılmaz",
    "customer_email": "ahmet@example.com",
    "customer_phone": "+905534062844",
    "product_type": "room",
    "check_in": "2026-03-01",
    "check_out": "2026-03-05",
    "nights": 4,
    "total_guests": 3,
    "adults": 2,
    "children": 1,
    "total_price": 18500,
    "status": "confirmed",
    "payment_status": "paid",
    "asistpro_reservation_id": "AP789",
    "asistpro_confirmation_code": "CONF123",
    
    // Kahvaltı bilgileri
    "includes_breakfast": true,
    "breakfast_days": 4,
    "breakfast_persons": 3,
    "breakfast_total_servings": 12,  // 4 gün × 3 kişi
    
    // Akşam yemeği bilgileri
    "includes_dinner": true,
    "dinner_days": 4,
    "dinner_persons": 3,
    "dinner_total_servings": 12,  // 4 gün × 3 kişi
    
    "meal_booking_ids": [
      "breakfast-101", "breakfast-102", 
      "breakfast-103", "breakfast-104",
      "dinner-105", "dinner-106", 
      "dinner-107", "dinner-108"
    ]
  }
}

// ŞEZLONG REZERVASYONU
{
  "event": "reservation_confirmed",
  "data": {
    "product_type": "sunbed",
    "reservation_id": "BOOKING987654",
    "booking_date": "2026-03-01",
    "total_price": 500,
    "items": {
      "sunbedCount": 2,
      "sunbeds": [
        {"row": "A", "number": 5},
        {"row": "A", "number": 6}
      ]
    }
  }
}

// KAHVALTI REZERVASYONU (Tek Başına)
{
  "event": "reservation_confirmed",
  "data": {
    "product_type": "breakfast",
    "reservation_id": "BOOKING555666",
    "booking_date": "2026-03-01",
    "total_price": 600,
    "items": {
      "totalPersons": 2,
      "adults": 2,
      "children": 0
    }
  }
}`}
            </pre>
          </div>

          {/* Save Button */}
          <div className="actions">
            <button 
              className="btn-save"
              onClick={handleSaveWebhookSettings}
              disabled={saving}
            >
              {saving ? 'Kaydediliyor...' : '💾 Webhook Ayarlarını Kaydet'}
            </button>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="tab-content">
          <div className="templates-info-box">
            <h4>📧 E-posta Şablonları Hakkında</h4>
            <p>Müşterilerinize gönderilecek e-postaların içeriğini özelleştirebilirsiniz. 
               Şablonlarda <code>{'{{değişken_adı}}'}</code> formatında değişkenler kullanabilirsiniz.</p>
          </div>

          <div className="templates-grid">
            <div className="templates-list">
              <h3>Şablonlar</h3>
              {templates.length === 0 ? (
                <div className="no-templates">
                  <p>⚠️ Şablon bulunamadı. Veritabanı migration'ı çalıştırın.</p>
                </div>
              ) : (
                templates.map(template => {
                  const label = TEMPLATE_LABELS[template.template_key] || { icon: '📄', name: template.template_key, description: '' };
                  return (
                    <div 
                      key={template.template_key}
                      className={`template-item ${selectedTemplate === template.template_key ? 'active' : ''}`}
                    >
                      <div className="template-content" onClick={() => handlePreviewTemplate(template.template_key)}>
                        <div className="template-header">
                          <span className="template-icon">{label.icon}</span>
                          <span className="template-name">{label.name}</span>
                        </div>
                        <span className="template-description">{label.description}</span>
                      </div>
                      <div className="template-actions">
                        <label className="toggle-mini">
                          <input 
                            type="checkbox" 
                            checked={template.is_active}
                            onChange={(e) => handleToggleTemplate(template.template_key, e.target.checked)}
                          />
                          <span className="toggle-slider-mini"></span>
                        </label>
                        <button 
                          className="btn-edit-template"
                          onClick={(e) => { e.stopPropagation(); handleEditTemplate(template.template_key); }}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="template-preview">
              {editingTemplate ? (
                <div className="template-editor">
                  <h3>✏️ Şablon Düzenle: {TEMPLATE_LABELS[editingTemplate.template_key]?.name || editingTemplate.template_key}</h3>
                  
                  <div className="form-group">
                    <label>E-posta Konusu</label>
                    <input 
                      type="text"
                      value={editingTemplate.subject}
                      onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                      placeholder="Örn: Rezervasyonunuz Onaylandı - {{hotel_name}}"
                    />
                  </div>

                  <div className="form-group">
                    <label>HTML İçerik</label>
                    <textarea 
                      value={editingTemplate.body_html}
                      onChange={(e) => setEditingTemplate({...editingTemplate, body_html: e.target.value})}
                      rows={15}
                      placeholder="HTML içerik..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Düz Metin Versiyonu (isteğe bağlı)</label>
                    <textarea 
                      value={editingTemplate.body_text || ''}
                      onChange={(e) => setEditingTemplate({...editingTemplate, body_text: e.target.value})}
                      rows={5}
                      placeholder="Düz metin içerik..."
                    />
                  </div>

                  <div className="variables-helper">
                    <h4>Kullanılabilir Değişkenler:</h4>
                    <div className="variables-list">
                      {(editingTemplate.variables || []).map((v: string) => (
                        <code key={v}>{`{{${v}}}`}</code>
                      ))}
                    </div>
                  </div>

                  <div className="editor-actions">
                    <button 
                      className="btn-cancel"
                      onClick={() => { setEditingTemplate(null); handlePreviewTemplate(editingTemplate.template_key); }}
                    >
                      İptal
                    </button>
                    <button 
                      className="btn-save"
                      onClick={handleSaveTemplate}
                      disabled={savingTemplate}
                    >
                      {savingTemplate ? 'Kaydediliyor...' : '💾 Kaydet'}
                    </button>
                  </div>
                </div>
              ) : templatePreview ? (
                <>
                  <div className="preview-header">
                    <h3>Önizleme</h3>
                    <span className="preview-subject">Konu: {templatePreview.subject}</span>
                  </div>
                  <div className="preview-frame">
                    <iframe
                      srcDoc={templatePreview.html}
                      title="Email Preview"
                      sandbox=""
                    />
                  </div>
                  <div className="preview-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => selectedTemplate && handleEditTemplate(selectedTemplate)}
                    >
                      ✏️ Bu Şablonu Düzenle
                    </button>
                  </div>
                </>
              ) : (
                <div className="no-preview">
                  <div className="no-preview-content">
                    <span className="no-preview-icon">👈</span>
                    <p>Önizlemek veya düzenlemek için<br/>sol taraftan bir şablon seçin</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Future: SMS & WhatsApp */}
      <div className="coming-soon-section">
        <h3>🚀 Yakında</h3>
        <div className="coming-soon-grid">
          <div className="coming-soon-item">
            <span className="icon">📱</span>
            <span>SMS Bildirimleri</span>
          </div>
          <div className="coming-soon-item">
            <span className="icon">💬</span>
            <span>WhatsApp Bildirimleri</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsTab;
